import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { RulerDimensionLine } from 'lucide-react';
import Hyperlink from '@ajat/design-system/Hyperlink';
import BulkQuantityInput from '@ajat/design-system/BulkQuantityInput';
import {
  HeadingLarge,
  StickyFooter,
} from '@ajat/design-system/Configurator';
import Gallery from '../Product/Gallery';
import SizeGuide from '../Product/SizeGuide';
import { RiskFreeBlock, DeliveryBlock } from '@ajat/design-system/SharedBlocks';
import useProductPage from '../Product/useProductPage';
import { useCart } from '../../../../context/CartContext';
import '../Product/ProductDetails.css';
import './BulkProduct.css';

/**
 * Shared BulkProduct page — brand-agnostic layout.
 *
 * Brand wrappers (e.g. `ABC-SE/BulkProduct`, `CLS-DK/BulkProduct`) pass the
 * `product` object, `brand`, `cartBrandSlug`, `priceSuffix`, localised `labels`,
 * `sizeGuide` config, and their `Header` component.
 *
 * Keeps identical UX across brands — only copy and brand tokens differ.
 */
const BulkProduct = ({
  product,
  brand,
  cartBrandSlug,
  priceSuffix,
  labels,
  sizeGuide,
  Header,
}) => {
  const { addItem } = useCart(cartBrandSlug);
  const [addingToCart, setAddingToCart] = useState(false);

  const [activeImage, setActiveImage] = useState(0);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [quantities, setQuantities] = useState(() =>
    (product?.sizes ?? []).reduce((acc, s) => ({ ...acc, [s]: 0 }), {})
  );
  const [quantityError, setQuantityError] = useState('');

  const images = product?.images ?? [];

  const {
    isZoomed, setIsZoomed,
    galleryRef, scrollYBeforeZoom,
    handleTouchStart, handleTouchEnd,
  } = useProductPage({
    imageCount: images.length,
    activeImage,
    setActiveImage,
    brand,
  });

  /* -- Totals -- */
  const totalQty = useMemo(
    () => Object.values(quantities).reduce((sum, v) => sum + (Number(v) || 0), 0),
    [quantities]
  );
  const totalPrice = totalQty * (product?.priceRaw ?? 0);

  const setSizeQty = useCallback((size, value) => {
    setQuantities((prev) => ({ ...prev, [size]: value }));
    if (quantityError) setQuantityError('');
  }, [quantityError]);

  const handleAddToCart = useCallback(async () => {
    const minTotal = product?.minTotal ?? 1;
    if (totalQty < minTotal) {
      setQuantityError(labels.minError(minTotal));
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.querySelector('.bulk-pp__size-section');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 80);
      });
      return;
    }
    if (addingToCart) return;
    setAddingToCart(true);
    try {
      const image = images[0]?.src || null;
      // One cart line per size with a quantity > 0
      await Promise.all(
        Object.entries(quantities)
          .filter(([, qty]) => qty > 0)
          .map(([size, qty]) =>
            addItem({
              itemType: 'product',
              productId: product.slug,
              unitPrice: product.priceRaw,
              additionalPrice: 0,
              options: {
                name: product.name,
                slug: product.slug,
                image,
                size,
              },
              quantity: qty,
            })
          )
      );
      document.dispatchEvent(new CustomEvent('open-minicart'));
    } catch (err) {
      console.error('Add to cart failed', err);
    } finally {
      setAddingToCart(false);
    }
  }, [totalQty, product, addingToCart, quantities, addItem, images, labels]);

  if (!product) {
    return (
      <div className="pp">
        <div className="pp__header"><Header hideOnScroll={false} /></div>
        <main className="pp__main" style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <p>{labels.notFound}</p>
        </main>
      </div>
    );
  }

  return (
    <div className={`pp bulk-pp ${isZoomed ? 'pp--zoomed' : ''}`}>
      {/* ═══ HEADER ═══ */}
      <div className="pp__header">
        <Header hideOnScroll={false} />
      </div>
      <div className="pp__header-mobile">
        <Header hideOnScroll={false} />
      </div>

      {/* ═══ MAIN ═══ */}
      <main className="pp__main">
        <Gallery
          alt={product?.name}
          images={images}
          activeImage={activeImage}
          setActiveImage={setActiveImage}
          isZoomed={isZoomed}
          setIsZoomed={setIsZoomed}
          scrollYBeforeZoom={scrollYBeforeZoom}
          galleryRef={galleryRef}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={handleTouchEnd}
        />

        <div className="pp__configurator">
          <HeadingLarge
            titleAs="h1"
            title={product.name}
            price={product.price}
            description={product.description}
          />

          <div className="pp__options">
            {/* ── Size quantities (bulk) ── */}
            <div className={`pp__section bulk-pp__size-section ${quantityError ? 'bulk-pp__size-section--error' : ''}`}>
              <div className="bulk-pp__size-heading">
                <h3 className="bulk-pp__size-title typo-h3">{labels.sizeTitle}</h3>
                <Hyperlink
                  variant="regular"
                  iconLeft={<RulerDimensionLine size={20} />}
                  showIconLeft
                  href="#"
                  onClick={(e) => { e.preventDefault(); setIsSizeGuideOpen(true); }}
                >
                  {labels.sizeGuide}
                </Hyperlink>
              </div>

              {product.minTotal > 0 && (
                <p className="bulk-pp__info typo-body-sm">
                  {labels.infoTemplate(product.minTotal)}
                </p>
              )}

              <div className="bulk-pp__size-list">
                {product.sizes.map((size) => (
                  <BulkQuantityInput
                    key={size}
                    sizeLabel={size}
                    value={quantities[size] ?? 0}
                    onChange={(v) => setSizeQty(size, v)}
                    min={0}
                    max={999}
                  />
                ))}
              </div>

              {quantityError && (
                <div className="bulk-pp__qty-error" role="alert">
                  <span className="typo-body-sm">{quantityError}</span>
                </div>
              )}
            </div>
          </div>

          {labels.riskFree && <RiskFreeBlock title={labels.riskFree.title} description={labels.riskFree.description} />}
          <DeliveryBlock time={labels.delivery.time} cost={labels.delivery.cost} description={labels.delivery.description} />
        </div>
      </main>

      {/* ═══ STICKY FOOTER ═══ */}
      <StickyFooter
        price={`${totalPrice}${priceSuffix}`}
        deliveryMessage={product?.deliveryInformation || null}
        deliveryTime={labels.delivery.time}
        deliveryPrice={labels.delivery.cost}
        deliveryText={labels.delivery.description}
        showDelivery={!!product?.deliveryInformation || !!labels.delivery.footer}
        onButtonClick={handleAddToCart}
        buttonLabel={labels.buttonLabel}
        buttonLoading={addingToCart}
        className="pp__footer"
      />

      {/* ═══ SIZE GUIDE ═══ */}
      <SizeGuide
        isOpen={isSizeGuideOpen}
        onClose={() => setIsSizeGuideOpen(false)}
        title={sizeGuide.title}
        items={sizeGuide.items}
      />
    </div>
  );
};

BulkProduct.propTypes = {
  product: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    priceRaw: PropTypes.number.isRequired,
    description: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.any, alt: PropTypes.string })),
    sizes: PropTypes.arrayOf(PropTypes.string).isRequired,
    minTotal: PropTypes.number,
  }),
  brand: PropTypes.oneOf(['CLS', 'ABC']).isRequired,
  cartBrandSlug: PropTypes.string.isRequired,
  priceSuffix: PropTypes.string.isRequired,
  labels: PropTypes.shape({
    sizeTitle: PropTypes.string.isRequired,
    sizeGuide: PropTypes.string.isRequired,
    infoTemplate: PropTypes.func.isRequired,
    buttonLabel: PropTypes.string.isRequired,
    minError: PropTypes.func.isRequired,
    notFound: PropTypes.string.isRequired,
    riskFree: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),   // null / omitted = no risk-free block (CLS DK)
    delivery: PropTypes.shape({
      time: PropTypes.string.isRequired,
      cost: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      footer: PropTypes.bool,   // also show it in the sticky footer (desktop)
    }).isRequired,
  }).isRequired,
  sizeGuide: PropTypes.shape({
    title: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }).isRequired,
  Header: PropTypes.elementType.isRequired,
};

export default BulkProduct;
